import { useTranslation } from "react-i18next";
import React from "react";
import { Modal, Typography } from "antd";
import Table from "../../../components/Common/dataTable/index";
import { formatMoney } from "../../../helpers/numberHelper";
import ExpensesStatusBadge from "components/Common/Badge/ExpensesStatusBadge";
import ActionsDropdown, { Action } from "components/Common/ActionsDropdown";
import { Expense } from "types/expenseTypes";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { formatToLocaleDate } from "utils/dateUtils";
import { useAbility } from "config/abilityContext";
import PaymentMethodDisplay from "components/Common/ui/PaymentMethodDisplay";

const { Text } = Typography;

interface Props {
  dataSource: Expense[];
  onEdit: (record: any) => void;
  onDelete: (id: string) => void;
  loading: boolean;
  hideActions?: boolean;
  dataPaginate?: any;
  onChange?: (pg: any) => void;
}

const { confirm } = Modal;
const ExpensesTable: React.FC<Props> = ({
  dataSource,
  onEdit,
  onDelete,
  loading,
  hideActions = false,
  dataPaginate,
  onChange
}) => {
  const { t } = useTranslation();
  const ability = useAbility();

  const columns = [
    {
      title: t("description"),
      dataIndex: "description",
      key: "description",
      width: "20%",
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: t("category"),
      dataIndex: ["supplier", "expenseCategory", "name"],
      key: "category",
      width: "15%",
    },
    {
      title: t("supplier"),
      dataIndex: ["supplier", "name"],
      key: "supplier",
      width: "15%",
    },
    {
      title: t("amount"),
      dataIndex: "amount",
      key: "amount",
      width: "15%",
      render: (amount: string) => (
        <span>{formatMoney(parseFloat(amount))}</span>
      ),
    },
     {
      title: t("paymentMethod.title"),
      key: "paymentMethod",
      width: "15%",
      align: "center",
      render: (_: any, record: any) => {
        return <PaymentMethodDisplay 
          payment={record}
        />;
      },
    },
    {
      title: t("date"),
      dataIndex: "expenseDate",
      key: "expenseDate",
      width: "15%",
      render: (expenseDate: string | undefined) => formatToLocaleDate(expenseDate),

    },
    {
      title: t("receipt"),
      dataIndex: "receiptFile",
      key: "receiptFile",
      width: "15%",
      render: (text: string, record: any) =>
        record.receiptFile && (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ cursor: "pointer", color: "#563a9d" }}
          >
            <i
              className="ti ti-download"
              style={{ cursor: "pointer" }}
              onClick={() => window.open(record.receiptFile, "_blank")}
            />
          </div>
        ),
    },
    {
      title: t("payment_status"),
      dataIndex: "computedStatus",
      key: "computedStatus",
      width: "20%",
      render: (computedStatus: string, record: any) => (
        <ExpensesStatusBadge
          status={computedStatus as "pending" | "approved" | "rejected" | "coming" | "overdue"}
        />
      ),
    },
    {
      title: t("action"),
      dataIndex: "action",
      hidden: hideActions,
      render: (_: string, record: any) => {
        const actions: Action[] = [
          {
            label: t("edit"),
            icon: "ti ti-edit",
            actionType: "link",
            onClick: () => onEdit(record),
            hidden: record.status !== "pending" || !ability.can("update", "Expense"),
          },
          {
            label: t("delete"),
            icon: "ti ti-trash",
            actionType: "link",
            onClick: () => showDeleteConfirm(record.id),
            hidden: record.status !== "pending" || !ability.can("delete", "Expense"),
          },
        ];
        if (record.status === "pending" && (ability.can("delete", "Expense") || ability.can("update", "Expense"))) {
          return <ActionsDropdown actions={actions} />;
        } else {
          return <i className="ti ti-lock"></i>;
        }
      },
    },
  ];

  const showDeleteConfirm = (id: string) => {
    confirm({
      title: t("are_you_sure_you_want_to_delete_this_expense?"),
      icon: <ExclamationCircleOutlined />,
      content: t("this_action_cannot_be_undone"),
      okText: t("delete"),
      okType: "danger",
      cancelText: t("cancel"),
      okButtonProps: { disabled: loading },
      onOk: () => onDelete(id),
    });
  };

  return (
    <div className="">
      <div className="table-responsive">
        <Table columns={columns} dataSource={dataSource} pagination={true}
          dataPaginate={dataPaginate}
          onChange={onChange} />
      </div>
    </div>
  );
};

export default ExpensesTable;
